import { Context } from '@blackbird/ui-base';
import React, { FC, useCallback, useContext, useState } from 'react';
import { P, match } from 'ts-pattern';

import ChallengeForm, {
  Challenge,
  ChallengeDetails,
  FormValues as ChallengeFormValues,
} from '@/components/forms/challenge';
import ForgotPasswordForm, { FormValues as ForgotPasswordValues } from '@/components/forms/forgot-password';
import LoginForm, { FormValues as LoginFormValues } from '@/components/forms/login';
import LoadingSplashScreen from '@/components/splash-screens/loading';
import SuccessfulSplashScreen from '@/components/splash-screens/successful';

export type FormValues = ChallengeFormValues | LoginFormValues | ForgotPasswordValues;

interface Properties {
  email?: string;
  onSubmit: (form: FormValues) => Promise<{ status: number; response: unknown } | undefined>;
}

const PasswordPolicy = {
  minimumLength: P.number,
  requireUppercase: P.boolean,
  requireLowercase: P.boolean,
  requireNumbers: P.boolean,
  requireSymbols: P.boolean,
  temporaryPasswordValidityDays: P.number,
};

const isChallengeFormSubmission = { challengeCode: P.string, password: P.string };
const isLoginFormSubmission = { password: P.string };

const AuthForm: FC<Properties> = ({ email: initialEmail, onSubmit: submitForm }) => {
  // These states are only to preserve form state through navigation and if a
  // submission fails
  const [email, setEmail] = useState<string | undefined>(initialEmail);
  const [password, setPassword] = useState<string | undefined>();
  const [challengeCode, setChallengeCode] = useState<string | undefined>();

  const [challenge, setChallenge] = useState<ChallengeDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState<null | string>(null);
  const snackbar = useContext(Context.Snackbar);

  const [hasForgottenPassword, setHasForgottenPassword] = useState(false);

  const forgotPassword = useCallback(() => {
    setHasForgottenPassword(true);
  }, []);

  const clearHasForgottenPassword = useCallback(() => {
    setHasForgottenPassword(false);
  }, []);

  const clearChallenge = useCallback(() => {
    setChallenge(null);
    clearHasForgottenPassword();
  }, []);

  const onSubmit = useCallback(async (form: FormValues) => {
    setLoading(true);
    setEmail(form.email);

    // State preservation when navigating forms after submission
    match(form as unknown)
      .with(isChallengeFormSubmission, ({ challengeCode, password }) => {
        setChallengeCode(challengeCode);
        setPassword(password);
      })
      .with(isLoginFormSubmission, ({ password }) => {
        setChallengeCode(password);
      });

    const timeout = new Promise((resolve) => setTimeout(resolve, 5_000));

    match(await Promise.race([submitForm(form), timeout]))
      .with(
        { status: 200, response: P.select({ authCallback: P.string, authorizationCode: P.string }) },
        ({ authCallback, authorizationCode }) => setLocation(`${authCallback}?authorizationCode=${authorizationCode}`),
      )
      .with(
        {
          status: P.number.between(400, 411),
          response: { message: P.select(P.string) },
        },
        (message) => snackbar.spawn(message, 'error'),
      )
      .with(
        {
          status: 412,
          response: P.select({
            challengeName: P.string.and(P.when((c: Challenge): c is Challenge => c in Challenge)),
            passwordPolicy: PasswordPolicy,
            session: P.string.or(null),
          }),
        },
        (challengeDetails) => {
          setChallenge(challengeDetails);
        },
      )
      .with(
        {
          status: 419,
          response: P.select({
            message: P.string,
            redirect: P.string,
          }),
        },
        ({ message, redirect }) => {
          snackbar.spawn(`${message} - Redirecting to ${redirect}...`, 'error');
          setTimeout(() => window.location.assign(redirect), 2500);
        },
      )
      .with(
        {
          status: 424,
        },
        () => {
          snackbar.spawn('MFA is not yet implemented...', 'error');
        },
      )
      .with(
        {
          response: { message: P.select(P.string) },
        },
        (message) => snackbar.spawn(`An error ocurred: ${message}`),
      )
      .otherwise(() => {
        snackbar.spawn('An error occured on sign-in', 'error');
      });

    setLoading(false);
  }, []);

  const splashScreen = match({ loading, location, challenge })
    .with({ loading: true, location: null }, ({ challenge }) => (
      <LoadingSplashScreen challenge={challenge && challenge.challengeName} />
    ))
    .with({ loading: false, location: P.string }, ({ location }) => (
      <SuccessfulSplashScreen navigate={() => window.location.assign(location)} />
    ))
    .otherwise(() => null);

  if (splashScreen) {
    return splashScreen;
  }

  if (email && challenge) {
    return (
      <ChallengeForm
        email={email}
        password={password}
        challengeCode={challengeCode}
        onSubmit={onSubmit}
        onCancel={clearChallenge}
        challenge={challenge}
      />
    );
  }

  if (hasForgottenPassword) {
    return <ForgotPasswordForm onSubmit={onSubmit} onCancel={clearHasForgottenPassword} />;
  }

  return <LoginForm defaultValues={{ email, password }} onForgotPassword={forgotPassword} onSubmit={onSubmit} />;
};

export default AuthForm;
